import { useContext, type ComponentProps } from 'react'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { Button, Popover, useGroupedButtonProps, Tooltip } from '@highlight/ui'
import { FeedbackContext } from '../index'
import { ThumbsUpSolid, ThumbsDownSolid } from './thumb-icons'

// `type` is the rating here, not the native button type — omit it so the two
// don't intersect to `never`.
type FeedbackTriggerProps = Omit<ComponentProps<typeof Button>, 'children' | 'type'> & {
  type: 'positive' | 'negative'
}

export function FeedbackTrigger({
  type,
  size,
  variant,
  tone,
  shape = 'square',
  className,
  ...props
}: FeedbackTriggerProps) {
  // On their own the thumbs are quiet 24px squares; inside a ButtonGroup the group decides.
  const resolved = useGroupedButtonProps({ variant, tone, size }, { variant: 'ghost', tone: 'neutral', size: 'sm' })
  const { setType, isSelected } = useContext(FeedbackContext)
  const isPositive = type === 'positive'
  const label = isPositive ? 'Good response' : 'Bad response'
  const Icon = isPositive ? (isSelected ? ThumbsUpSolid : ThumbsUp) : isSelected ? ThumbsDownSolid : ThumbsDown

  return (
    <Popover.Trigger className="shrink-0" render={<span />} nativeButton={false} onClick={() => setType(type)}>
      {/* Inside the trigger, so the popover still anchors to the span and the
          tooltip only wraps the button it describes. */}
      <Tooltip>
        <Tooltip.Trigger
          render={
            <Button
              aria-label={label}
              aria-pressed={isSelected}
              {...resolved}
              shape={shape}
              className={className}
              {...props}
            />
          }
        >
          <Icon />
        </Tooltip.Trigger>
        <Tooltip.Content>{label}</Tooltip.Content>
      </Tooltip>
    </Popover.Trigger>
  )
}
